<!-- Responsibility: Handle user invitations for alpha/beta testers.
Scope: Form container for sending invites. -->
<admin-invite-card>
  <section class="admin-card">
    <header>
      <h2>User Invitations</h2>
      <span class="admin-card-meta">Automated email mechanism for alpha/beta testers.</span>
    </header>
    <p class="admin-copy">Enter a tester's email address to record their access and (later) trigger an automated welcome email.</p>
    <div ref="inviteHost" class="admin-form-host"></div>
    <div class="admin-actions">
      <button 
        type="button" 
        class="action-btn primary" 
        onclick={ onSendInvite } 
        disabled={ props.sendingInvite }
      >
        { props.sendingInvite ? 'SENDING...' : 'SEND INVITE' }
      </button>
    </div>
    <p if={ props.inviteMessage } class="admin-note">{ props.inviteMessage }</p>
  </section>

  <script>
    import { AutoForm, collectValues, populateFields } from 'field-assist'
    import { buildAdminInviteFormMeta } from '../../helpers/admin-invite-form'

    export default {
      onMounted() {
        this.renderForm()
      },

      onUpdated() {
        // Only re-render if host is empty (e.g. after a reset or if it was cleared)
        const host = this.$('[ref="inviteHost"]')
        if (host && !host.hasChildNodes()) {
          this.renderForm()
        }
      },

      renderForm() {
        const host = this.$('[ref="inviteHost"]')
        if (!(host instanceof HTMLElement)) return
        host.replaceChildren()
        const autoForm = new AutoForm(buildAdminInviteFormMeta())
        autoForm.render({ email: '' }, host)
        populateFields(host, { email: '' }, 'name')

        // Listen for Enter key
        const input = host.querySelector('input[name="email"]')
        if (input) {
          input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') this.onSendInvite()
          })
        }
      },

      onSendInvite() {
        const host = this.$('[ref="inviteHost"]')
        if (!(host instanceof HTMLElement)) return
        const input = host.querySelector('input[type="email"]')
        const values = collectValues(host, undefined, 'name')
        const email = String(
          input instanceof HTMLInputElement && input.value
            ? input.value
            : values.email ?? '',
        ).trim()
        
        if (this.props.onSendInvite) {
          this.props.onSendInvite(email)
        }
      }
    }
  </script>

  <style>
    .admin-card {
      display: grid;
      gap: var(--space-sm);
      border: 1px solid color-mix(in srgb, var(--border) 46%, transparent);
      border-radius: 1rem;
      padding: var(--space-md);
      background:
        linear-gradient(180deg, color-mix(in srgb, var(--surface-raised) 88%, transparent), color-mix(in srgb, var(--bg) 88%, transparent)),
        radial-gradient(circle at top right, color-mix(in srgb, var(--gold) 14%, transparent), transparent 58%);
      box-shadow: var(--shadow-elev-2);
    }

    header {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      gap: var(--space-sm);
    }

    h2 {
      margin: 0;
      color: var(--text-prose);
    }

    .admin-card-meta,
    .admin-copy,
    .admin-note {
      color: var(--text-muted);
      font-size: var(--type-sm, 0.875rem);
      line-height: 1.6;
      margin: 0;
    }

    .admin-form-host {
      display: grid;
      gap: var(--space-sm);
    }

    .admin-actions {
      display: flex;
      flex-wrap: wrap;
      justify-content: flex-end;
      gap: var(--space-xs);
    }
  </style>
</admin-invite-card>
